import React, {
  useCallback,
  MouseEventHandler,
  useRef,
  ComponentProps,
} from 'react'

import ItemCardModalMenu from './MenuModal'
import { EllipsisMenuProps } from '../TripItemTile/BaseTripItemTile'

import IconButton from 'components/Luxkit/Button/IconButton'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Dropdown from 'components/Luxkit/Dropdown/v2/Dropdown'
import LineEllipsisVIcon from 'components/Luxkit/Icons/line/LineEllipsisVIcon'
import useToggle from 'hooks/useToggle'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props extends EllipsisMenuProps {
  className?: string
  kind?: ComponentProps<typeof IconButton>['kind']
  size?: ComponentProps<typeof IconButton>['size']
  variant?: ComponentProps<typeof IconButton>['variant']
  shape?: ComponentProps<typeof IconButton>['shape']
}

export default function MenuButton({
  className,
  title,
  subtitle,
  bodyContent,
  footerContent,
  kind = 'tertiary',
  size = 'small',
  variant = 'dark',
  shape = 'circle',
}: Props) {
  const openModal = useOpenModal()

  const isMobileScreen = useIsMobileScreen()

  const {
    value: dropdownOpen,
    toggle: toggleDropdown,
    off: closeDropdown,
  } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggleMenu: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (isMobileScreen) {
        openModal(
          ItemCardModalMenu,
          { bodyContent, footerContent, title, subtitle },
          { height: 'auto' },
        )
      } else {
        toggleDropdown()
      }
    },
    [
      isMobileScreen,
      openModal,
      bodyContent,
      footerContent,
      toggleDropdown,
      title,
      subtitle,
    ],
  )

  return (
    <>
      <IconButton
        kind={kind}
        size={size}
        variant={variant}
        shape={shape}
        data-testid="action-menu-button"
        aria-label="action menu"
        onClick={toggleMenu}
        className={className}
        ref={triggerRef}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <Dropdown
        triggerRef={triggerRef}
        open={dropdownOpen}
        onClose={closeDropdown}
        position="bottom"
        onClick={stopPropagation}
      >
        <DropdownContainer>
          {bodyContent}
          {footerContent}
        </DropdownContainer>
      </Dropdown>
    </>
  )
}
